import '../globalStyles.css';
import './LeftPane.css';

import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';
import * as environment from '../../../environment';
import { EditorBackButton } from "./EditorBackButton";
import { ApplicationSelectDropdown } from './ApplicationSelectDropdown';
import { AppCheckerConsole } from './AppCheckerConsole';
import { LeftPaneContext, ILeftPaneContext, Navigate } from './LeftPaneContext';
import { LeftPaneNavigation } from './LeftPaneNavigation';
import { LeftPaneSelectedTab, LeftPaneSelectedSection } from '../../../constants';
import { TreeView } from './tree_view/TreeView';
import { ButtonConfig } from "./commandButtonsConfig";
import { Application, PythonApplication, MachinelogicLibrary, ApplicationBase } from '../../../machinelogic/types';
import { CollapsibleStateContextProvider } from "../../../components/collapsible_state/CollapsibleStateContext";
import { ApplicationTypes } from '../../../machinelogic';

declare global {
  interface Window {
    _main: {
      ifTouchScreen: () => boolean;
    }
  }
}


interface LeftPaneProps {
  library: MachinelogicLibrary;
  user: any;
  commandButtonsConfig: ButtonConfig[];
  wiringMethods: {
    goBack: (app: Application) => void;
    legacyWidgetRenderFunction?: (treeViewContainerRef: any) => any;
    onAppSelected?: ((app: ApplicationBase) => void) | undefined;
    pendantNavigate: Navigate;
    applicationTreeWidget: (library: MachinelogicLibrary, application: Application, onCopySequence: () => void, pUser, singleAppMode: boolean) => any;
    applicationErrorsWidget: (app: Application, pendantNavigate: Navigate) => any;
    openSequence: (library, application, sequence) => void;
    showConfirmationModal: (title: string, message: string, onConfirm: () => void) => void;
    openDocument: (application: Application) => void;
    onApplicationUploaded: (app: Application | PythonApplication | null) => void;
  };
}

export const LeftPane: FC<LeftPaneProps> = observer(({ user, library, commandButtonsConfig, wiringMethods }) => {
  const [isApplicationInEditMode, setIsApplicationInEditMode] = useState(false);
  const [selectedTab, setSelectedTab] = useState(LeftPaneSelectedTab.TREE_VIEW);
  const [selectedSection, setSelectedSection] = useState(LeftPaneSelectedSection.MAIN_PROGRAM);
  const currentApplication = library.currentApplication;

  const navigateToReferencedSequence = (sequenceName: string) => {
    if(library.currentApplication.isReadOnly()) { return; }
    const sequence = library.currentApplication.getSequence(sequenceName);
    setSelectedSection(LeftPaneSelectedSection.SEQUENCES);
    wiringMethods.openSequence(library, currentApplication, sequence);
    currentApplication.selectSequenceByName(sequenceName);
  };

  const leftPaneContext: ILeftPaneContext = {
    isApplicationInEditMode,
    setIsApplicationInEditMode,
    selectedTab,
    setSelectedTab,
    selectedSection,
    setSelectedSection,
    library,
    commandButtonsConfig,
    pendantNavigate: wiringMethods.pendantNavigate,
    applicationErrorsWidget: wiringMethods.applicationErrorsWidget,
    openSequence: wiringMethods.openSequence,
    invalidInstructionUuids: currentApplication.invalidInstructionUuids,
    showConfirmationModal: wiringMethods.showConfirmationModal,
    openDocument: wiringMethods.openDocument,
    isRunning: currentApplication.isRunning(),
    navigateToReferencedSequence
  };


  useEffect(() => {
    const currentApplication = library.currentApplication;
    leftPaneContext.invalidInstructionUuids = currentApplication?.invalidInstructionUuids;
  }, [currentApplication?.invalidInstructionUuids]);

  // this should be removed once we revamp the right pane.
  // the right pane should not rely on the tree view to know what to render.
  const initialSequence = currentApplication.initialSequence;
  useEffect(() => {
    const currentApplication = library.currentApplication;
    leftPaneContext.openSequence(library, currentApplication, currentApplication.initialSequence);
    setSelectedSection(LeftPaneSelectedSection.MAIN_PROGRAM);

    if(currentApplication.isReadOnly()) { return; }

    currentApplication.selectSequenceByName(initialSequence.name);
  },[currentApplication]);

  let content = <></>;
  if (selectedTab === LeftPaneSelectedTab.TREE_VIEW) {
    content = <TreeView />;
  } else if (selectedTab === LeftPaneSelectedTab.ERRORS) {
    content = <AppCheckerConsole />;
  }

  const goBack = () => {
    wiringMethods.goBack(library.currentApplication);
    library.selectApplication(null);
  };

  return (
    <LeftPaneContext.Provider value={leftPaneContext}>
      <CollapsibleStateContextProvider>
        <div className={cx('left-pane-container', {
          'environment-cad': environment.isOnCad(),
          'environment-pendant': environment.isOnPendant(),
          'environment-touchscreen': window._main?.ifTouchScreen?.(),
        })}>
          {<EditorBackButton goBack={goBack} disabled={library.currentApplication?.isRunning()}/>}
          <div className="ml_library v_layout bordered spaced">
            <div className="library-widget-container v_layout">
              <ApplicationSelectDropdown
                library={leftPaneContext.library}
                onAppSelected={wiringMethods.onAppSelected}
                onApplicationUploaded={wiringMethods.onApplicationUploaded}
                isRunning={leftPaneContext.isRunning}
                applicationType={library.currentApplication.getType()}
                user={user}
                allowAppCreation={library.currentApplication.getType() === ApplicationTypes.MachineLogic}
              />
              <LeftPaneNavigation/>
              {content}
            </div>
          </div>
        </div>
      </CollapsibleStateContextProvider>
    </LeftPaneContext.Provider>
  );
});